import React, { useState, useEffect } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PHONE_PLACEHOLDER_MASK } from '../../config/region';
import { Lead, ValidationError } from './newRunTypes';

interface ErrorRowEditorProps {
  leads: Lead[];
  validationErrors: ValidationError[];
  onSaveCorrections: (correctedLeads: Lead[]) => void;
  onCancel: () => void;
}

interface EditableLead extends Lead {
  originalRow: number;
  errors: string[];
}

const ErrorRowEditor: React.FC<ErrorRowEditorProps> = ({
  leads,
  validationErrors,
  onSaveCorrections,
  onCancel,
}) => {
  const [initialErrorCount] = useState(validationErrors.length);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Create editable leads from error rows
  const [editableLeads, setEditableLeads] = useState<EditableLead[]>(() => {
    const errorRowNumbers = new Set(validationErrors.map(e => e.row));
    return leads
      .map((lead, index) => ({
        ...lead,
        originalRow: index + 2, // +2 because CSV row 1 is header, array is 0-indexed
        errors: validationErrors
          .filter(e => e.row === index + 2)
          .map(e => e.field === 'general' ? e.message : `${e.field}: ${e.message}`),
      }))
      .filter(lead => errorRowNumbers.has(lead.originalRow));
  });

  const handleFieldChange = (index: number, field: keyof Lead, value: string) => {
    const updated = [...editableLeads];
    updated[index] = { ...updated[index], [field]: value };
    setEditableLeads(updated);
  };

  const handleSave = () => {
    // Create updated leads array with corrections
    const correctedLeads = [...leads];
    editableLeads.forEach(editedLead => {
      const arrayIndex = editedLead.originalRow - 2;
      if (arrayIndex >= 0 && arrayIndex < correctedLeads.length) {
        correctedLeads[arrayIndex] = {
          name: editedLead.name,
          phone: editedLead.phone,
          email: editedLead.email,
        };
      }
    });
    onSaveCorrections(correctedLeads);
  };

  // Track when validation errors change after save
  useEffect(() => {
    const currentErrorCount = validationErrors.length;
    
    // If all errors are fixed, show success and close editor
    if (currentErrorCount === 0 && initialErrorCount > 0) {
      setSuccessMessage(`All errors fixed successfully!`);
      setTimeout(() => {
        setSuccessMessage(null);
        onCancel();
      }, 2000);
      return;
    }
    
    // If some errors were fixed but more remain
    if (currentErrorCount < initialErrorCount && currentErrorCount > 0) {
      const fixedCount = initialErrorCount - currentErrorCount;
      setSuccessMessage(`${fixedCount} ${fixedCount === 1 ? 'error' : 'errors'} fixed successfully!`);
      setTimeout(() => setSuccessMessage(null), 4000);
      
      // Update editable leads with new error state
      const errorRowNumbers = new Set(validationErrors.map(e => e.row));
      const updated = leads
        .map((lead, index) => ({
          ...lead,
          originalRow: index + 2,
          errors: validationErrors
            .filter(e => e.row === index + 2)
            .map(e => e.field === 'general' ? e.message : `${e.field}: ${e.message}`),
        }))
        .filter(lead => errorRowNumbers.has(lead.originalRow));
      setEditableLeads(updated);
    }
  }, [validationErrors, leads, initialErrorCount, onCancel]);

  return (
    <div className="mt-4 bg-slate-800/50 border border-white/20 rounded-lg p-4">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-white">
            Fix Errors ({editableLeads.length} {editableLeads.length === 1 ? 'row' : 'rows'})
          </h4>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-300 bg-white/5 border border-white/20 rounded hover:bg-white/10 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded hover:from-cyan-600 hover:to-blue-700 transition-colors"
            >
              <CheckIcon className="h-4 w-4" />
              Save & Validate
            </button>
          </div>
        </div>
        {successMessage && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
            <CheckIcon className="h-4 w-4 text-green-400 flex-shrink-0" />
            <span className="text-sm text-green-400">{successMessage}</span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto scrollbar-hide">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-xs font-medium text-gray-400 pb-2 pr-4">Row</th>
              <th className="text-left text-xs font-medium text-gray-400 pb-2 pr-4">Name *</th>
              <th className="text-left text-xs font-medium text-gray-400 pb-2 pr-4">Phone *</th>
              <th className="text-left text-xs font-medium text-gray-400 pb-2">Email</th>
            </tr>
          </thead>
          <tbody>
            {editableLeads.map((lead, index) => (
              <React.Fragment key={index}>
                <tr className="border-b border-white/5">
                  <td className="py-3 pr-4 text-gray-400 align-top">{lead.originalRow}</td>
                  <td className="py-3 pr-4">
                    <input
                      type="text"
                      value={lead.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      className="w-full px-2 py-1.5 bg-slate-900/60 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="Enter name"
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <input
                      type="text"
                      value={lead.phone}
                      onChange={(e) => handleFieldChange(index, 'phone', e.target.value)}
                      className="w-full px-2 py-1.5 bg-slate-900/60 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder={PHONE_PLACEHOLDER_MASK}
                    />
                  </td>
                  <td className="py-3">
                    <input
                      type="text"
                      value={lead.email}
                      onChange={(e) => handleFieldChange(index, 'email', e.target.value)}
                      className="w-full px-2 py-1.5 bg-slate-900/60 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="email@example.com"
                    />
                  </td>
                </tr>
                {lead.errors.length > 0 && (
                  <tr>
                    <td></td>
                    <td colSpan={3} className="pb-3">
                      <div className="text-xs text-red-400 space-y-1">
                        {lead.errors.map((error, errIdx) => (
                          <div key={errIdx}>• {error}</div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-400">
        Make your corrections above and click "Save & Validate" to check if the errors are fixed.
      </p>
    </div>
  );
};

export default ErrorRowEditor;
